import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookie } from "../contexts/CookieContext";
import ReactPixel from "react-facebook-pixel";

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

/**
 * Reads analytics consent synchronously from localStorage.
 * Used as a fallback for the initial render before CookieContext hydrates.
 *
 * @returns {boolean}
 */
const getStoredAnalyticsConsent = () => {
  try {
    const stored = localStorage.getItem("cookie-preferences");
    return stored ? JSON.parse(stored).analytics === true : false;
  } catch {
    return false;
  }
};

/**
 * Initializes the Meta Pixel and tracks PageView events, gated behind
 * the user's analytics cookie consent (same gate as GA4).
 *
 * Handles two scenarios:
 * - Returning visitor: consent is already in localStorage, so the Pixel is
 *   initialized on the first render cycle without waiting for CookieContext
 *   to hydrate from its own useEffect.
 * - New visitor: Pixel is initialized the moment the user accepts cookies,
 *   which causes cookiePreferences.analytics to flip to true.
 *
 * PageView is fired once on init and again on every subsequent route change.
 *
 * @returns {void}
 */
const useMetaPixel = () => {
  const location = useLocation();
  const { cookiePreferences } = useCookie();
  const initializedRef = useRef(false);

  // Determine consent: prefer live context value, fall back to localStorage
  // for the initial render before CookieContext has hydrated.
  const hasConsent = cookiePreferences?.analytics === true || getStoredAnalyticsConsent();

  /**
   * Initializes the Meta Pixel once per session if consent is granted.
   * @returns {boolean} Whether the pixel is initialized after this call
   */
  const initPixel = () => {
    if (initializedRef.current || !META_PIXEL_ID || !hasConsent) return initializedRef.current;
    ReactPixel.init(META_PIXEL_ID, {}, { autoConfig: true, debug: false });
    initializedRef.current = true;
    return true;
  };

  // Initialize and fire first PageView on mount and when consent changes
  useEffect(() => {
    if (initPixel()) {
      ReactPixel.pageView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsent]);

  // Fire PageView on route changes after initialization
  useEffect(() => {
    if (!initializedRef.current) return;
    ReactPixel.pageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};

export default useMetaPixel;
